import { LoadingSpinner, PageHeaderWrapper, useModalState } from "@clipboard-health/ui-components";
import { isDefined, isNil } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import { RootPaths } from "@src/appV2/App/paths";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { useEffect } from "react";
import { useHistory } from "react-router-dom";

import { useGetPayoutSettings } from "../../Accounts/PayoutSettings/api/useGetPayoutSettings";
import { useGoToStripeOnboarding } from "../../Accounts/PayoutSettings/hooks/useGoToStripeOnboarding";
import { PageHeaderTopRow } from "../../components/PageHeaderTopRow";
import { PageWrapper } from "../../components/PageWrapper";
import { useGetPaymentServiceAccount } from "../../Onboarding/api/useGetPaymentServiceAccount";
import { useUpdatePaymentServiceAccount } from "../../Onboarding/api/useUpdatePaymentServiceAccount";
import { SHIFT_DISCOVERY_LIST_PATH } from "../../ShiftDiscovery/paths";
import { IncompleteStripeSetupPageContent } from "./components/IncompleteStripeSetupPageContent";
import { StripeAccountVerifiedPageContent } from "./components/StripeAccountVerifiedPageContent";
import { StripeAccountVerifyingPageContent } from "./components/StripeAccountVerifyingPageContent";
import { StripeOnboardingCompletePageContent } from "./components/StripeOnboardingCompletePageContent";
import { StripeOnboardingIntroPageContent } from "./components/StripeOnboardingIntroPageContent";
import { StripeOnboardingPayoutSettingsBottomSheet } from "./components/StripeOnboardingPayoutSettingsBottomSheet";

const POLLING_INTERVAL_MS = 5000;

export function StripeOnboardingPage() {
  const history = useHistory();

  const isStripeOnboardingEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_NEW_STRIPE_ONBOARDING, {
    defaultValue: false,
  });

  const { goToStripeOnboarding, isGoToStripeOnboardingLoading } = useGoToStripeOnboarding();
  const { mutateAsync: updatePaymentServiceAccount, isLoading: isCompleteOnboardingLoading } =
    useUpdatePaymentServiceAccount({
      meta: {
        logErrorMessage: APP_V2_APP_EVENTS.UPDATE_PAYMENT_SERVICE_ACCOUNT_FAILURE,
        userErrorMessage: "Something went wrong, please try again.",
      },
    });

  const { data: { stripeOnboardingStage } = {}, isLoading } = useGetPaymentServiceAccount({
    refetchInterval: POLLING_INTERVAL_MS,
    staleTime: 0,
  });

  const { data: payoutSettings, isLoading: isPayoutSettingsLoading } = useGetPayoutSettings({
    includeExternalAccount: true,
  });

  const isOnboardingFullyCompleted =
    stripeOnboardingStage === "completed" &&
    isDefined(payoutSettings?.attributes.preferredExternalAccountStripeId);

  const payoutSettingsBottomSheetModalState = useModalState();

  const shouldExit = !isStripeOnboardingEnabled || (!isLoading && isNil(stripeOnboardingStage));

  useEffect(() => {
    if (shouldExit) {
      history.goBack();
    }
  }, [shouldExit, history]);

  if (shouldExit) {
    return null;
  }

  return (
    <PageWrapper variant="tertiary">
      <PageHeaderWrapper variant="tertiary">
        <PageHeaderTopRow
          title="Set up Stripe"
          hideBackButton={isOnboardingFullyCompleted}
          onClickBack={() => {
            history.goBack();
          }}
        />
      </PageHeaderWrapper>

      {isLoading && (
        <Box sx={{ display: "flex", flex: 1, alignItems: "center", justifyContent: "center" }}>
          <LoadingSpinner size="large" />
        </Box>
      )}
      {!isLoading && stripeOnboardingStage === "initial" && (
        <StripeOnboardingIntroPageContent
          isSetUpStripeLoading={isGoToStripeOnboardingLoading}
          setUpStripe={goToStripeOnboarding}
        />
      )}
      {!isLoading && stripeOnboardingStage === "waiting" && (
        <StripeAccountVerifyingPageContent
          isReturnToStripeLoading={isGoToStripeOnboardingLoading}
          onReturnToStripeClick={goToStripeOnboarding}
          onExploreTheAppClick={history.goBack}
        />
      )}
      {!isLoading && stripeOnboardingStage === "validation_failed" && (
        <IncompleteStripeSetupPageContent
          isCompleteStripeSetupLoading={isGoToStripeOnboardingLoading}
          onCompleteStripeSetupClick={goToStripeOnboarding}
        />
      )}
      {!isLoading && stripeOnboardingStage === "completed" && isPayoutSettingsLoading && (
        <Box sx={{ display: "flex", flex: 1, alignItems: "center", justifyContent: "center" }}>
          <LoadingSpinner size="large" />
        </Box>
      )}
      {!isLoading &&
        stripeOnboardingStage === "completed" &&
        !isPayoutSettingsLoading &&
        isNil(payoutSettings?.attributes.preferredExternalAccountStripeId) && (
          <StripeAccountVerifiedPageContent
            onConfigurePayoutSettingsClick={payoutSettingsBottomSheetModalState.openModal}
          />
        )}
      {stripeOnboardingStage === "completed" && (
        <StripeOnboardingPayoutSettingsBottomSheet
          modalState={payoutSettingsBottomSheetModalState}
        />
      )}
      {!isLoading &&
        stripeOnboardingStage === "completed" &&
        !isPayoutSettingsLoading &&
        isDefined(payoutSettings?.attributes.preferredExternalAccountStripeId) &&
        isDefined(payoutSettings?.destinationDetails) && (
          <StripeOnboardingCompletePageContent
            payoutSchedule={payoutSettings.attributes.payoutSchedule}
            payoutFee={payoutSettings.meta.instantPayoutFee}
            destination={payoutSettings.destinationDetails}
            isStartBookingLoading={isCompleteOnboardingLoading}
            onStartBooking={async () => {
              try {
                await updatePaymentServiceAccount({ isOnboardingCompleted: true });
                history.push(`${RootPaths.APP_V2_HOME}/${SHIFT_DISCOVERY_LIST_PATH}`);
              } catch {
                // Error toast is shown via the mutation's meta.userErrorMessage
              }
            }}
          />
        )}
    </PageWrapper>
  );
}
